import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, FormControlLabel, IconButton, InputAdornment, MenuItem, Skeleton, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import {
  MdAccountCircle, MdAdd, MdAdminPanelSettings, MdAllInbox, MdAssessment, MdAssignmentReturn, MdAttachMoney, MdBusiness, MdCampaign, MdCancel, MdClose,
  MdDashboard, MdDeleteOutline, MdDescription, MdDevices, MdEdit, MdEmail, MdEventNote, MdExpandLess, MdExpandMore, MdHelp, MdInventory2, MdLayers, MdListAlt,
  MdLocalShipping, MdMenu, MdMessage, MdNotifications, MdPeople, MdPerson, MdPlaylistAddCheck, MdRedeem, MdSearch, MdSecurity, MdSettings, MdShoppingCart,
  MdSms, MdStore, MdSubdirectoryArrowRight, MdVisibility, MdWork,
} from 'react-icons/md';
import { deleteData, getData, postData, putData } from '../../Webservice/Webservice';

const FONT = 'Mulish, sans-serif';

const ICONS = {
  MdDashboard: [MdDashboard, 'Dashboard'], MdPerson: [MdPerson, 'Users / Profile'], MdStore: [MdStore, 'Store / Supplier'], MdBusiness: [MdBusiness, 'Seller / Business'],
  MdInventory2: [MdInventory2, 'Inventory'], MdListAlt: [MdListAlt, 'List / Catalog'], MdShoppingCart: [MdShoppingCart, 'Orders / Sales'], MdAssessment: [MdAssessment, 'Reports / Analytics'],
  MdAdminPanelSettings: [MdAdminPanelSettings, 'Admin / Roles'], MdLocalShipping: [MdLocalShipping, 'Shipping / Logistics'], MdPlaylistAddCheck: [MdPlaylistAddCheck, 'Tasks / Checks'],
  MdAllInbox: [MdAllInbox, 'Packing / Inbox'], MdAssignmentReturn: [MdAssignmentReturn, 'Returns'], MdSettings: [MdSettings, 'Settings'], MdMenu: [MdMenu, 'Menu config'],
  MdCancel: [MdCancel, 'Cancel / Expired'], MdAccountCircle: [MdAccountCircle, 'Account'], MdNotifications: [MdNotifications, 'Notifications'], MdAttachMoney: [MdAttachMoney, 'Payout / Finance'],
  MdLayers: [MdLayers, 'Categories / Layers'], MdDevices: [MdDevices, 'Mobile content'], MdDescription: [MdDescription, 'Policy / Terms'], MdHelp: [MdHelp, 'FAQs / Support'],
  MdEventNote: [MdEventNote, 'Activity logs'], MdSecurity: [MdSecurity, 'Security / Login log'], MdRedeem: [MdRedeem, 'Rewards / Coupons'], MdCampaign: [MdCampaign, 'Campaign / Marketing'],
  MdSms: [MdSms, 'SMS'], MdEmail: [MdEmail, 'Email'], MdMessage: [MdMessage, 'WhatsApp'], MdPeople: [MdPeople, 'HR / Employee'], MdWork: [MdWork, 'Work / Payroll'],
};
const MenuIcon = ({ name, size = 18 }) => {
  const Cmp = (ICONS[name] || ICONS.MdDashboard)[0];
  return <Cmp size={size} />;
};

const authToken = () => {
  const raw = sessionStorage.getItem('token') || localStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const yellowBtn = { textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } };
const iconBtn = (c) => ({ color: c, background: `${c}14`, borderRadius: '8px', '&:hover': { background: `${c}26` } });
const head = { fontFamily: FONT, fontWeight: 800, fontSize: 12.5, color: '#475569', background: '#f8fafc', whiteSpace: 'nowrap' };
const cell = { fontFamily: FONT, fontSize: 13.5 };

const Kpi = ({ title, value, from, to, active, onClick }) => (
  <Box onClick={onClick} sx={{ flex: 1, minWidth: 160, p: 2, borderRadius: '16px', color: '#fff', background: `linear-gradient(135deg,${from},${to})`, cursor: onClick ? 'pointer' : 'default', outline: active ? '3px solid #0f172a' : 'none', outlineOffset: 2 }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, opacity: 0.9, fontWeight: 700 }}>{title}</Typography>
    <Typography sx={{ fontFamily: FONT, fontSize: 26, fontWeight: 800, lineHeight: 1.15 }}>{value}</Typography>
  </Box>
);

const Card = ({ title, accent, children }) => (
  <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, borderRadius: '14px', p: 2.25 }}>
    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1 }}>{title}</Typography>
    {children}
  </Box>
);

const StatusChip = ({ status }) => {
  const on = (status || 'active') === 'active';
  return <Chip size="small" label={on ? 'Active' : 'Inactive'} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: on ? '#dcfce7' : '#f1f5f9', color: on ? '#166534' : '#475569' }} />;
};

const emptyForm = { id: null, name: '', parentId: '', menuFor: 'admin', status: 'active', icon: 'MdDashboard', assignToRoles: [] };

const Settings = () => {
  const [platform, setPlatform] = useState('admin');
  const [menus, setMenus] = useState([]); // tree
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState('');
  const [expanded, setExpanded] = useState({});

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [usage, setUsage] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, r] = await Promise.all([
        getData('menu/list', { menuFor: platform }, authToken(), false),
        getData('role/list', { limit: 200, roleFor: platform }, authToken(), false),
      ]);
      setMenus(Array.isArray(m) ? m : []);
      setRoles(r?.roles || []);
    } catch {
      setMenus([]);
    } finally {
      setLoading(false);
    }
  }, [platform]);
  useEffect(() => { load(); }, [load]);

  const flat = useMemo(() => menus.flatMap((m) => [m, ...(m.submenus || [])]), [menus]);
  const stats = useMemo(() => ({
    main: menus.length,
    sub: flat.length - menus.length,
    active: flat.filter((m) => (m.status || 'active') === 'active').length,
    inactive: flat.filter((m) => m.status === 'inactive').length,
    unassigned: flat.filter((m) => !m.roleCount).length,
  }), [menus, flat]);

  // filtering keeps a parent visible when one of its submenus matches
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const ok = (m, isSub) => {
      if (q && !String(m.name).toLowerCase().includes(q)) return false;
      if (status && (m.status || 'active') !== status) return false;
      if (usage === 'assigned' && !m.roleCount) return false;
      if (usage === 'unassigned' && m.roleCount) return false;
      if (type === 'main' && isSub) return false;
      if (type === 'sub' && !isSub) return false;
      return true;
    };
    const out = [];
    menus.forEach((m) => {
      const subs = (m.submenus || []).filter((s) => ok(s, true));
      const self = ok(m, false);
      const filtering = q || status || usage || type;
      if (self || subs.length) {
        out.push({ menu: m, isSub: false, dim: !self });
        const open = filtering ? true : expanded[m._id] !== false;
        if (open) subs.forEach((s) => out.push({ menu: s, isSub: true, parent: m }));
      }
    });
    return out;
  }, [menus, search, status, type, usage, expanded]);

  const dirty = !!(search || status || type || usage);

  // ---- form ----
  const rolesWith = (id) => roles.filter((r) => (r.menus || []).some((x) => String(x._id || x) === String(id))).map((r) => String(r._id));
  const openForm = (menu = null, parent = null) => {
    setErrors({});
    setFormError('');
    setViewing(null);
    if (menu) setForm({ id: menu._id, name: menu.name, parentId: menu.parentId || '', menuFor: menu.menuFor || platform, status: menu.status || 'active', icon: menu.icon || 'MdDashboard', assignToRoles: rolesWith(menu._id) });
    else setForm({ ...emptyForm, menuFor: platform, parentId: parent?._id || '', assignToRoles: [] });
    setFormOpen(true);
  };
  const toggleRole = (id) => setForm((f) => ({ ...f, assignToRoles: f.assignToRoles.includes(id) ? f.assignToRoles.filter((x) => x !== id) : [...f.assignToRoles, id] }));

  const save = async (ev) => {
    ev.preventDefault();
    setFormError('');
    const e = {};
    if (!form.name.trim()) e.name = 'Menu name is required';
    else if (form.name.trim().length > 60) e.name = 'Keep it under 60 characters';
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), menuFor: form.menuFor, parentId: form.parentId || null, status: form.status, icon: form.icon, assignToRoles: form.assignToRoles };
      if (form.id) await putData(`menu/update/${form.id}`, payload, authToken());
      else await postData('menu/add', payload, authToken());
      setFormOpen(false);
      setNotice(form.id ? 'Menu updated.' : 'Menu created.');
      load();
    } catch (err) {
      setFormError(err?.message || 'Could not save the menu');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (menu) => {
    try {
      await putData(`menu/update/${menu._id}`, { status: (menu.status || 'active') === 'active' ? 'inactive' : 'active' }, authToken());
      load();
    } catch { /* toast shown by the service */ }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteData(`menu/delete/${toDelete._id}`, authToken());
      setToDelete(null);
      setNotice('Menu deleted.');
      load();
    } catch (err) {
      setDeleteError(err?.message || 'Could not delete the menu');
    } finally {
      setDeleting(false);
    }
  };

  const openView = async (menu) => {
    setViewing({ ...menu, roles: roles.filter((r) => rolesWith(menu._id).includes(String(r._id))) });
    try {
      const full = await getData(`menu/${menu._id}`, null, authToken(), false);
      if (full?._id) setViewing((cur) => (cur && cur._id === full._id ? { ...cur, ...full } : cur));
    } catch { /* keep the local copy */ }
  };

  const parentName = (id) => menus.find((m) => String(m._id) === String(id))?.name;
  const mainChoices = menus.filter((m) => String(m._id) !== String(form.id));

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Menu configuration</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>The sidebar menus each app offers, and which roles can open them.</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <ToggleButtonGroup exclusive size="small" value={platform} onChange={(_, v) => v && setPlatform(v)}>
            <ToggleButton value="admin" sx={{ textTransform: 'none', fontWeight: 700, px: 2.5 }}>Admin CRM</ToggleButton>
            <ToggleButton value="warehouse" sx={{ textTransform: 'none', fontWeight: 700, px: 2.5 }}>Warehouse</ToggleButton>
          </ToggleButtonGroup>
          <Button startIcon={<MdAdd />} variant="contained" onClick={() => openForm()} sx={yellowBtn}>Add menu</Button>
        </Box>
      </Box>

      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Kpi title="Main menus" value={stats.main} from="#6366f1" to="#4338ca" active={type === 'main'} onClick={() => setType(type === 'main' ? '' : 'main')} />
        <Kpi title="Submenus" value={stats.sub} from="#0ea5e9" to="#0369a1" active={type === 'sub'} onClick={() => setType(type === 'sub' ? '' : 'sub')} />
        <Kpi title="Active" value={stats.active} from="#22c55e" to="#15803d" active={status === 'active'} onClick={() => setStatus(status === 'active' ? '' : 'active')} />
        <Kpi title="Inactive" value={stats.inactive} from="#94a3b8" to="#475569" active={status === 'inactive'} onClick={() => setStatus(status === 'inactive' ? '' : 'inactive')} />
        <Kpi title="Not given to any role" value={stats.unassigned} from="#f59e0b" to="#b45309" active={usage === 'unassigned'} onClick={() => setUsage(usage === 'unassigned' ? '' : 'unassigned')} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.75, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
        <TextField size="small" placeholder="Search menu name…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 220 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
        <TextField select size="small" label="Type" value={type} onChange={(e) => setType(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="">All types</MenuItem><MenuItem value="main">Main menus</MenuItem><MenuItem value="sub">Submenus</MenuItem>
        </TextField>
        <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="">All statuses</MenuItem><MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
        <TextField select size="small" label="Role access" value={usage} onChange={(e) => setUsage(e.target.value)} sx={{ minWidth: 190 }}>
          <MenuItem value="">Any</MenuItem><MenuItem value="assigned">Given to a role</MenuItem><MenuItem value="unassigned">Given to no role</MenuItem>
        </TextField>
        {dirty && <Button onClick={() => { setSearch(''); setStatus(''); setType(''); setUsage(''); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Clear</Button>}
      </Box>

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={head}>Menu</TableCell>
                <TableCell sx={head}>Type</TableCell>
                <TableCell sx={head} align="center">Roles</TableCell>
                <TableCell sx={head}>Status</TableCell>
                <TableCell sx={head}>Added</TableCell>
                <TableCell sx={head} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && [...Array(6)].map((_, i) => <TableRow key={i}>{[...Array(6)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
              {!loading && rows.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>No menus match these filters.</TableCell></TableRow>}
              {!loading && rows.map(({ menu, isSub, parent, dim }) => (
                <TableRow key={menu._id} hover sx={{ background: isSub ? '#fafbff' : 'transparent', opacity: dim ? 0.6 : 1 }}>
                  <TableCell sx={cell}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pl: isSub ? 4 : 0 }}>
                      {isSub ? <MdSubdirectoryArrowRight size={16} color="#94a3b8" /> : (
                        <IconButton size="small" onClick={() => setExpanded((x) => ({ ...x, [menu._id]: x[menu._id] === false }))} disabled={!(menu.submenus || []).length} sx={{ visibility: (menu.submenus || []).length ? 'visible' : 'hidden' }}>
                          {expanded[menu._id] === false ? <MdExpandMore size={18} /> : <MdExpandLess size={18} />}
                        </IconButton>
                      )}
                      <Box sx={{ background: '#e0e7ff', color: '#3730a3', borderRadius: '8px', p: 0.75, display: 'flex' }}><MenuIcon name={menu.icon} /></Box>
                      <Box>
                        <Typography sx={{ fontFamily: FONT, fontWeight: isSub ? 600 : 800, fontSize: 13.5 }}>{menu.name}</Typography>
                        {!isSub && (menu.submenus || []).length > 0 && <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{menu.submenus.length} submenu{menu.submenus.length === 1 ? '' : 's'}</Typography>}
                        {isSub && parent && <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>under {parent.name}</Typography>}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={cell}>{isSub ? 'Submenu' : 'Main menu'}</TableCell>
                  <TableCell align="center"><Chip size="small" label={menu.roleCount || 0} sx={{ fontWeight: 800, height: 22, background: menu.roleCount ? '#dcfce7' : '#fef3c7', color: menu.roleCount ? '#166534' : '#92400e' }} /></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Switch size="small" checked={(menu.status || 'active') === 'active'} onChange={() => toggleStatus(menu)} />
                      <StatusChip status={menu.status} />
                    </Box>
                  </TableCell>
                  <TableCell sx={cell}>{fmtDate(menu.createdAt)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {!isSub && <Tooltip title="Add submenu"><IconButton size="small" onClick={() => openForm(null, menu)} sx={iconBtn('#16a34a')}><MdAdd size={17} /></IconButton></Tooltip>}{' '}
                    <Tooltip title="View"><IconButton size="small" onClick={() => openView(menu)} sx={iconBtn('#4f46e5')}><MdVisibility size={17} /></IconButton></Tooltip>{' '}
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openForm(menu)} sx={iconBtn('#ca8a04')}><MdEdit size={17} /></IconButton></Tooltip>{' '}
                    <Tooltip title="Delete"><IconButton size="small" onClick={() => { setDeleteError(''); setToDelete(menu); }} sx={iconBtn('#dc2626')}><MdDeleteOutline size={17} /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* create / edit */}
      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} maxWidth="sm" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, borderBottom: '3px solid #6366f1', background: '#fff' }}>
          <Box sx={{ background: '#e0e7ff', color: '#3730a3', borderRadius: '10px', p: 1, display: 'flex' }}><MenuIcon name={form.icon} size={22} /></Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 20 }}>{form.id ? 'Edit menu' : form.parentId ? 'Add submenu' : 'Add menu'}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{form.parentId ? `Under ${parentName(form.parentId) || 'a main menu'}` : 'Shown in the sidebar'}</Typography>
          </Box>
          <IconButton onClick={() => setFormOpen(false)} disabled={saving} size="small"><MdClose size={20} /></IconButton>
        </Box>
        <Box component="form" onSubmit={save} noValidate sx={{ display: 'contents' }}>
          <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
            <Card title="Menu details" accent="#6366f1">
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mt: 1 }}>
                <TextField size="small" label="Menu name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} error={!!errors.name} helperText={errors.name} sx={{ gridColumn: { sm: '1 / -1' } }} />
                <TextField select size="small" label="Platform" value={form.menuFor} disabled={!!form.id} onChange={(e) => setForm({ ...form, menuFor: e.target.value, parentId: '', assignToRoles: [] })} helperText={form.id ? 'Cannot change after creating' : ''}>
                  <MenuItem value="admin">Admin CRM</MenuItem><MenuItem value="warehouse">Warehouse</MenuItem>
                </TextField>
                <TextField select size="small" label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem>
                </TextField>
                <TextField select size="small" label="Parent menu" value={form.parentId} onChange={(e) => setForm({ ...form, parentId: e.target.value })} sx={{ gridColumn: { sm: '1 / -1' } }} helperText="Leave empty to make this a main menu">
                  <MenuItem value="">None (main menu)</MenuItem>
                  {mainChoices.filter((m) => m.menuFor === form.menuFor).map((m) => <MenuItem key={m._id} value={m._id}>{m.name}</MenuItem>)}
                </TextField>
              </Box>
            </Card>

            <Card title="Icon" accent="#0ea5e9">
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(46px, 1fr))', gap: 1, mt: 1 }}>
                {Object.entries(ICONS).map(([key, [Cmp, label]]) => (
                  <Tooltip key={key} title={label}>
                    <Box onClick={() => setForm({ ...form, icon: key })} sx={{ cursor: 'pointer', display: 'flex', justifyContent: 'center', p: 1.1, borderRadius: '10px', border: '2px solid', borderColor: form.icon === key ? '#6366f1' : '#e2e8f0', background: form.icon === key ? '#eef2ff' : '#fff', color: form.icon === key ? '#4338ca' : '#475569' }}><Cmp size={20} /></Box>
                  </Tooltip>
                ))}
              </Box>
              <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8', mt: 1 }}>The admin sidebar picks icons by menu name; the warehouse app uses this choice.</Typography>
            </Card>

            <Card title={`Roles that can open it (${form.assignToRoles.length})`} accent="#f59e0b">
              <Box sx={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                {roles.filter((r) => (r.roleFor || 'admin') === form.menuFor).map((r) => (
                  <FormControlLabel key={r._id} control={<Checkbox size="small" checked={form.assignToRoles.includes(String(r._id))} onChange={() => toggleRole(String(r._id))} />}
                    label={<Typography sx={{ fontFamily: FONT, fontSize: 13.5, textTransform: 'capitalize' }}>{String(r.name).replace(/_/g, ' ')}</Typography>} />
                ))}
                {roles.filter((r) => (r.roleFor || 'admin') === form.menuFor).length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>No roles on this platform yet.</Typography>}
              </Box>
            </Card>
            {formError && <Alert severity="error">{formError}</Alert>}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
            <Button onClick={() => setFormOpen(false)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving} sx={yellowBtn}>{saving ? 'Saving…' : form.id ? 'Update menu' : 'Create menu'}</Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* view */}
      <Dialog open={!!viewing} onClose={() => setViewing(null)} maxWidth="sm" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        {viewing && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, background: 'linear-gradient(135deg,#6366f1,#4338ca)', color: '#fff' }}>
              <Box sx={{ background: 'rgba(255,255,255,.2)', borderRadius: '10px', p: 1, display: 'flex' }}><MenuIcon name={viewing.icon} size={22} /></Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 20 }}>{viewing.name}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, opacity: 0.9 }}>{viewing.parentId ? `Submenu of ${parentName(viewing.parentId) || '—'}` : 'Main menu'} · {viewing.menuFor === 'warehouse' ? 'Warehouse' : 'Admin CRM'}</Typography>
              </Box>
              <IconButton onClick={() => setViewing(null)} size="small" sx={{ color: '#fff' }}><MdClose size={20} /></IconButton>
            </Box>
            <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Card title="Details" accent="#0ea5e9">
                {[['Status', <StatusChip key="s" status={viewing.status} />], ['Icon', (ICONS[viewing.icon] || ICONS.MdDashboard)[1]], ['Added', fmtDate(viewing.createdAt)], ['Last updated', fmtDate(viewing.updatedAt)]].map(([k, v]) => (
                  <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px dashed #e2e8f0' }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{k}</Typography>
                    <Typography component="div" sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700 }}>{v}</Typography>
                  </Box>
                ))}
              </Card>
              <Card title={`Roles with access (${(viewing.roles || []).length})`} accent="#f59e0b">
                {(viewing.roles || []).length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>No role can open this menu yet.</Typography>}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {(viewing.roles || []).map((r) => <Chip key={r._id} size="small" label={String(r.name).replace(/_/g, ' ')} sx={{ fontFamily: FONT, fontWeight: 700, textTransform: 'capitalize', background: '#e0e7ff', color: '#3730a3' }} />)}
                </Box>
              </Card>
              {!viewing.parentId && (
                <Card title={`Submenus (${(viewing.submenus || []).length})`} accent="#22c55e">
                  {(viewing.submenus || []).length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>No submenus.</Typography>}
                  {(viewing.submenus || []).map((s) => (
                    <Box key={s._id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.9, borderBottom: '1px dashed #e2e8f0' }}>
                      <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600 }}>{s.name}</Typography>
                      <StatusChip status={s.status} />
                    </Box>
                  ))}
                </Card>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={() => setViewing(null)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Close</Button>
              <Button onClick={() => openForm(viewing)} startIcon={<MdEdit />} variant="contained" sx={yellowBtn}>Edit</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* delete */}
      <Dialog open={!!toDelete} onClose={() => !deleting && setToDelete(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, mb: 1 }}>Delete menu?</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#475569' }}>
            <b>{toDelete?.name}</b>{(toDelete?.submenus || []).length ? ` and its ${toDelete.submenus.length} submenu(s)` : ''} will be removed for good, and taken away from every role that has {(toDelete?.submenus || []).length ? 'them' : 'it'}.
            To hide it without losing it, mark it Inactive instead.
          </Typography>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setToDelete(null)} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={confirmDelete} disabled={deleting} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>{deleting ? 'Deleting…' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;
